import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import Layout from '@/components/Layout';
import { Button } from '@/components/ui/button';
import CancelarConsultaModal from '@/components/CancelarConsultaModal';
import { 
  ArrowLeft, 
  Calendar, 
  Clock, 
  User, 
  Phone, 
  Mail,
  X,
  CheckCircle,
  AlertCircle,
  Tag,
  XCircle,
  UserX,
  HeartPulse,
  Trash2,
  MapPin,
  Monitor,
  ClipboardCheck,
  RotateCcw,
  DollarSign,
  Plus
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export default function ConsultaDetalhesPage({ user, onLogout, showConfig }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const [consulta, setConsulta] = useState(null);
  const [paciente, setPaciente] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [showFaltaModal, setShowFaltaModal] = useState(false);
  const [showExcluirModal, setShowExcluirModal] = useState(false);
  const [processing, setProcessing] = useState(false);
  const isMasterAccess = user?.is_master_access === true;

  // Financeiro
  const [pagamentos, setPagamentos] = useState([]);
  const [totalPago, setTotalPago] = useState(0);
  const [valorTotal, setValorTotal] = useState(0);
  const [showLancamentoModal, setShowLancamentoModal] = useState(false);
  const [lancamentoForm, setLancamentoForm] = useState({ valor: '', forma_pagamento: 'pix', data_pagamento: '', observacao: '' });
  const [valorTotalEdit, setValorTotalEdit] = useState('');
  const [savingLancamento, setSavingLancamento] = useState(false);
  const valorTotalInputRef = useRef(null);
  const valorRecebidoInputRef = useRef(null);

  // Retorno (financeiro vinculado à consulta original)
  const [retornoInfo, setRetornoInfo] = useState({
    is_retorno: false,
    consulta_origem_id: null,
    data_consulta_origem: '',
    hora_inicio_origem: '',
    categoria_origem: ''
  });

  useEffect(() => {
    fetchConsulta();
  }, [id]);

  const fetchConsulta = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/consultas/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setConsulta(response.data);
      setValorTotal(response.data.valor_total || 0);
      
      if (response.data.paciente_id) {
        try {
          const pacienteResponse = await axios.get(`${API}/pacientes/${response.data.paciente_id}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setPaciente(pacienteResponse.data);
        } catch (err) {
          console.log('Paciente não encontrado');
        }
      }
      loadPagamentos();
    } catch (error) {
      toast.error('Erro ao carregar consulta');
      navigate('/agenda');
    } finally {
      setLoading(false);
    }
  };

  const loadPagamentos = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/consultas/${id}/pagamentos`, { headers: { Authorization: `Bearer ${token}` } });
      setPagamentos(res.data.pagamentos || []);
      setTotalPago(res.data.total_pago || 0);
      setValorTotal(res.data.valor_total || 0);
      setRetornoInfo({
        is_retorno: !!res.data.is_retorno,
        consulta_origem_id: res.data.consulta_origem_id || null,
        data_consulta_origem: res.data.data_consulta_origem || '',
        hora_inicio_origem: res.data.hora_inicio_origem || '',
        categoria_origem: res.data.categoria_origem || ''
      });
    } catch { /* ignore */ }
  };

  const handleSaveLancamento = async () => {
    const valor = parseFloat(lancamentoForm.valor.replace(/[^\d.,]/g, '').replace(',', '.'));
    if (!valor || valor <= 0) { toast.error('Informe um valor válido'); return; }
    const vtEdit = parseFloat(valorTotalEdit.replace(/[^\d.,]/g, '').replace(',', '.'));
    const vtEfetivo = (!isNaN(vtEdit) && vtEdit > 0) ? vtEdit : valorTotal;
    if (!vtEfetivo || vtEfetivo <= 0) {
      toast.error('Configure o Valor Total da consulta antes de registrar um lançamento');
      return;
    }
    const restanteAtual = vtEfetivo - totalPago;
    if (valor > restanteAtual + 0.001) {
      toast.error(`Valor excede o restante (R$ ${Math.max(restanteAtual, 0).toFixed(2).replace('.', ',')})`);
      return;
    }
    setSavingLancamento(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/consultas/${id}/pagamentos`, {
        valor,
        valor_total: vtEdit > 0 ? vtEdit : undefined,
        forma_pagamento: lancamentoForm.forma_pagamento,
        data_pagamento: lancamentoForm.data_pagamento || new Date().toISOString().split('T')[0],
        observacao: lancamentoForm.observacao
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Lançamento registrado!');
      setShowLancamentoModal(false);
      setLancamentoForm({ valor: '', forma_pagamento: 'pix', data_pagamento: '', observacao: '' });
      loadPagamentos();
      fetchConsulta();
    } catch { toast.error('Erro ao registrar lançamento'); }
    finally { setSavingLancamento(false); }
  };

  // Erro inline do modal
  const lancamentoErro = (() => {
    if (!showLancamentoModal) return null;
    const valorNum = parseFloat((lancamentoForm.valor || '').replace(/[^\d.,]/g, '').replace(',', '.'));
    const vtEdit = parseFloat((valorTotalEdit || '').replace(/[^\d.,]/g, '').replace(',', '.'));
    const vtEfetivo = (!isNaN(vtEdit) && vtEdit > 0) ? vtEdit : valorTotal;
    if (!vtEfetivo || vtEfetivo <= 0) return 'Informe o Valor Total da consulta';
    const restanteAtual = vtEfetivo - totalPago;
    if (!isNaN(valorNum) && valorNum > restanteAtual + 0.001) {
      return `Valor excede o restante: R$ ${Math.max(restanteAtual, 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    }
    return null;
  })();

  // Erro de Valor Total (não pode ser menor que total já pago)
  const valorTotalErro = (() => {
    if (!showLancamentoModal) return null;
    const vtEdit = parseFloat((valorTotalEdit || '').replace(/[^\d.,]/g, '').replace(',', '.'));
    if (isNaN(vtEdit)) return null;
    if (vtEdit < 0) return 'Valor inválido';
    if (totalPago > 0 && vtEdit < totalPago - 0.001) {
      return `Menor que o já recebido (R$ ${Number(totalPago).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })})`;
    }
    return null;
  })();

  const handleDeletePagamento = async (pagamentoId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/consultas/${id}/pagamentos/${pagamentoId}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Lançamento cancelado');
      loadPagamentos();
    } catch { toast.error('Erro ao cancelar lançamento'); }
  };

  const handleSaveValorTotal = async (newVal) => {
    const val = parseFloat(String(newVal).replace(/[^\d.,]/g, '').replace(',', '.'));
    if (isNaN(val) || val < 0) {
      toast.error('Valor inválido');
      return false;
    }
    if (val < totalPago - 0.001) {
      toast.error(`Valor Total não pode ser menor que o já recebido (${formatCurrency(totalPago)})`);
      return false;
    }
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/consultas/${id}/valor-total`, { valor_total: val }, { headers: { Authorization: `Bearer ${token}` } });
      setValorTotal(val);
      toast.success('Valor Total atualizado');
      return true;
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao atualizar valor total');
      return false;
    }
  };

  const [savingValorTotal, setSavingValorTotal] = useState(false);
  const handleSalvarValorTotalButton = async () => {
    setSavingValorTotal(true);
    const ok = await handleSaveValorTotal(valorTotalEdit);
    setSavingValorTotal(false);
    if (ok) {
      // Sincronizar para formatação
      const val = parseFloat(String(valorTotalEdit).replace(/[^\d.,]/g, '').replace(',', '.'));
      setValorTotalEdit(String(val));
    }
  };

  const openLancamentoModal = () => {
    setLancamentoForm({ valor: '', forma_pagamento: 'pix', data_pagamento: new Date().toISOString().split('T')[0], observacao: '' });
    setValorTotalEdit(valorTotal > 0 ? String(valorTotal) : '');
    setShowLancamentoModal(true);
  };

  // Auto-focus: Valor Total se vazio, senão Valor Recebido
  useEffect(() => {
    if (!showLancamentoModal) return;
    const t = setTimeout(() => {
      const vtEmpty = !valorTotal || valorTotal <= 0;
      if (vtEmpty && valorTotalInputRef.current) {
        valorTotalInputRef.current.focus();
        valorTotalInputRef.current.select?.();
      } else if (valorRecebidoInputRef.current) {
        valorRecebidoInputRef.current.focus();
      }
    }, 80);
    return () => clearTimeout(t);
  }, [showLancamentoModal, valorTotal]);

  const formatCurrency = (v) => {
    const n = Number(v) || 0;
    return `R$ ${n.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const handleIniciarAtendimento = () => {
    setShowConfirmModal(true);
  };

  const handleConfirmarAtendimento = async () => {
    setProcessing(true);
    try {
      const token = localStorage.getItem('token');
      
      await axios.get(`${API}/consultas/${id}/marcar-realizada`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Navigate to prontuario - toast will be shown there
      navigate(`/pacientes/prontuario/${consulta.paciente_id}?novaQueixa=true`);
      
    } catch (error) {
      toast.error('Erro ao iniciar atendimento');
      setProcessing(false);
    }
  };

  const handleCancelledByModal = (updatedConsulta) => {
    navigate(`/agenda?data=${consulta.data_consulta}`, {
      state: { toast: 'Consulta cancelada', updatedConsulta }
    });
  };

  const handleMarcarFalta = async () => {
    // Optimistic: update UI immediately
    const previousConsulta = consulta;
    setShowFaltaModal(false);
    setConsulta(prev => prev ? { ...prev, status: 'falta' } : prev);
    toast.success('Consulta marcada como falta');
    
    // Fire API in background
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/consultas/${id}`, {
        status: 'falta'
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
    } catch (error) {
      // Rollback on failure
      setConsulta(previousConsulta);
      toast.error('Erro ao marcar falta');
    }
  };

  const handleExcluirConsulta = async () => {
    setShowExcluirModal(false);
    setProcessing(true);
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/consultas/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Consulta excluída permanentemente!');
      navigate(`/agenda?data=${consulta.data_consulta}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao excluir consulta');
      setProcessing(false);
    }
  };

  const handleExcluirBloqueio = async () => {
    setProcessing(true);
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/consultas/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Bloqueio excluído com sucesso!');
      navigate(`/agenda?data=${consulta.data_consulta}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao excluir bloqueio');
      setProcessing(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      const [year, month, day] = dateStr.split('-');
      return `${day}/${month}/${year}`;
    } catch {
      return dateStr;
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      agendada: 'bg-yellow-100 text-yellow-800 border-yellow-300',
      confirmada: 'bg-green-100 text-green-800 border-green-300',
      realizada: 'bg-gray-100 text-gray-800 border-gray-300',
      cancelada: 'bg-red-100 text-red-800 border-red-300',
      falta: 'bg-amber-100 text-amber-800 border-amber-300'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const getStatusLabel = (status) => {
    const labels = {
      agendada: 'Agendada',
      confirmada: 'Confirmada',
      realizada: 'Realizada',
      cancelada: 'Cancelada',
      falta: 'Paciente Faltou'
    };
    return labels[status] || status;
  };

  if (loading) {
    return (
      <Layout user={user} onLogout={onLogout} showConfig={showConfig}>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
        </div>
      </Layout>
    );
  }

  if (!consulta) {
    return (
      <Layout user={user} onLogout={onLogout} showConfig={showConfig}>
        <div className="text-center py-12">
          <p className="text-gray-500">Consulta não encontrada</p>
          <Button onClick={() => navigate('/agenda')} className="mt-4">
            Voltar para Agenda
          </Button>
        </div>
      </Layout>
    );
  }

  const canStartAppointment = consulta.status === 'agendada' || consulta.status === 'confirmada';
  const canCancel = consulta.status === 'agendada' || consulta.status === 'confirmada';
  const canMarkFalta = consulta.status === 'agendada' || consulta.status === 'confirmada';
  const isBlocked = consulta.categoria?.toLowerCase() === 'horário bloqueado';

  return (
    <Layout user={user} onLogout={onLogout} showConfig={showConfig}>
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <button
            onClick={() => navigate(`/agenda?data=${consulta.data_consulta}`)}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            <span className="hidden sm:inline">Voltar para Agenda</span>
            <span className="sm:hidden">Voltar</span>
          </button>
          
          <span className={`px-3 py-1 rounded-full text-sm font-medium border ${isBlocked ? 'text-gray-700 bg-gray-100 border-gray-300' : getStatusColor(consulta.status)}`}>
            {isBlocked ? 'Bloqueado' : getStatusLabel(consulta.status)}
          </span>
        </div>

        {/* Main Card */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          {/* Banner de Retorno */}
          {retornoInfo.is_retorno && retornoInfo.consulta_origem_id && (
            <button
              type="button"
              onClick={() => navigate(`/consulta/${retornoInfo.consulta_origem_id}`)}
              className="w-full flex items-center justify-between gap-3 px-6 py-3 bg-purple-50 border-b border-purple-100 hover:bg-purple-100 transition-colors text-left"
              data-testid="banner-retorno-origem"
            >
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-purple-100 ring-1 ring-purple-200 flex items-center justify-center flex-shrink-0">
                  <RotateCcw className="w-4 h-4 text-purple-700" />
                </div>
                <div className="min-w-0">
                  <p className="text-[11px] font-semibold uppercase tracking-wider text-purple-700">Retorno</p>
                  <p className="text-sm font-medium text-gray-800 truncate">
                    Consulta original em <span className="font-semibold">{formatDate(retornoInfo.data_consulta_origem) || '—'}</span>
                    {retornoInfo.hora_inicio_origem && <> às <span className="font-semibold">{retornoInfo.hora_inicio_origem}</span></>}
                  </p>
                </div>
              </div>
              <span className="text-xs font-semibold text-purple-700 hover:underline whitespace-nowrap">
                Ver consulta original →
              </span>
            </button>
          )}

          {/* Consulta Header */}
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 px-6 py-5 border-b border-gray-100">
            <h1 className="text-xl sm:text-2xl font-bold text-gray-900 mb-1">
              {isBlocked ? 'Horário Bloqueado' : consulta.categoria}
            </h1>
            <div className="flex flex-wrap items-center gap-4 text-gray-700">
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                <span>{formatDate(consulta.data_consulta)}</span>
              </div>
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4" />
                <span>{consulta.hora_inicio} - {consulta.hora_fim}</span>
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="p-6">
            {!isBlocked && (
              <>
                {/* Paciente Info */}
                <div className="mb-6">
                <h2 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">
                    Paciente
                  </h2>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                        <User className="w-6 h-6 text-blue-600" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="font-semibold text-gray-900 text-lg">
                          {consulta.paciente_nome || 'Paciente não identificado'}
                        </h3>
                        {paciente && (
                          <div className="mt-2 space-y-1">
                            {paciente.telefone && (
                              <div className="flex items-center gap-2 text-gray-600">
                                <Phone className="w-4 h-4" />
                                <span>{paciente.telefone}</span>
                              </div>
                            )}
                            {paciente.email && (
                              <div className="flex items-center gap-2 text-gray-600">
                                <Mail className="w-4 h-4" />
                                <span>{paciente.email}</span>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Detalhes Grid */}
                <div className="mb-6">
                  <div className="bg-gray-50 rounded-lg p-4">
                    <div className="flex items-center gap-2 text-gray-500 mb-1">
                      <Tag className="w-4 h-4" />
                      <span className="text-sm">Procedimento</span>
                    </div>
                    <p className="font-medium text-gray-900">
                      {consulta.categoria || 'Não informado'}
                    </p>
                  </div>
                </div>

                {/* Modo de Atendimento */}
                {consulta.modo_atendimento && (
                  <div className="mb-6" data-testid={`detail-modo-${consulta.id}`}>
                    <div className="bg-gray-50 rounded-lg p-4">
                      <div className="flex items-center gap-2 text-gray-500 mb-1">
                        {consulta.modo_atendimento === 'online' ? (
                          <Monitor className="w-4 h-4" />
                        ) : (
                          <MapPin className="w-4 h-4" />
                        )}
                        <span className="text-sm">Modo de Atendimento</span>
                      </div>
                      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-slate-100 border border-slate-300 text-slate-600 text-sm font-semibold">
                        {consulta.modo_atendimento === 'online' ? (
                          <>
                            <Monitor className="w-4 h-4" />
                            Online
                          </>
                        ) : (
                          <>
                            <MapPin className="w-4 h-4" />
                            Presencial
                          </>
                        )}
                      </span>
                    </div>
                  </div>
                )}

                {/* Preventivo - only when authorized */}
                {consulta.preventivo === 'sim' && (
                  <div className="mb-6" data-testid={`detail-preventivo-${consulta.id}`}>
                    <div className="bg-gray-50 rounded-lg p-4">
                      <div className="flex items-center gap-2 text-gray-500 mb-1">
                        <ClipboardCheck className="w-4 h-4" />
                        <span className="text-sm">Preventivo</span>
                      </div>
                      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-slate-100 border border-slate-300 text-slate-600 text-sm font-semibold">
                        <ClipboardCheck className="w-4 h-4" />
                        Preventivo autorizado
                      </span>
                    </div>
                  </div>
                )}

                {/* Tipo de Consulta - Retorno */}
                {(consulta.tipo_consulta === 'retorno' || consulta.tipo_cobranca === 'retorno_gratuito') && (
                  <div className="mb-6" data-testid={`detail-retorno-${consulta.id}`}>
                    <div className="bg-teal-50 rounded-lg p-4 border border-teal-200">
                      <div className="flex items-center gap-2 text-teal-700 mb-1">
                        <RotateCcw className="w-4 h-4" />
                        <span className="text-sm">Tipo de Consulta</span>
                      </div>
                      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-teal-100 border border-teal-300 text-teal-700 text-sm font-semibold">
                        <RotateCcw className="w-4 h-4" />
                        Retorno
                      </span>
                    </div>
                  </div>
                )}

                {/* Info de retorno gratuito */}
                {consulta.status === 'realizada' && consulta.tipo_cobranca === 'retorno_gratuito' && consulta.consulta_origem_id && (
                  <div className="mb-6">
                    <div className="bg-green-50 rounded-lg p-4 border border-green-200">
                      <div className="flex items-center gap-2 text-green-700">
                        <CheckCircle className="w-5 h-5" />
                        <span className="font-medium">Retorno gratuito utilizado</span>
                      </div>
                    </div>
                  </div>
                )}

                {/* Confirmação de Presença - Lembrete de comparecimento */}
                <div className="mb-6" data-testid="confirmacao-presenca-section">
                  <h2 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">
                    Confirmação de Presença
                  </h2>
                  {(() => {
                    const isConfirmed = consulta.status === 'confirmada' || 
                      consulta.status_confirmacao?.toLowerCase() === 'confirmado';
                    
                    if (consulta.status_confirmacao || consulta.data_confirmacao || consulta.resposta_confirmacao || isConfirmed) {
                      return (
                        <div className={`rounded-lg p-4 border ${
                          isConfirmed 
                            ? 'bg-green-50 border-green-200' 
                            : 'bg-yellow-50 border-yellow-200'
                        }`}>
                          <div className={`flex items-center gap-2 ${
                            isConfirmed ? 'text-green-700' : 'text-yellow-700'
                          }`}>
                            {isConfirmed 
                              ? <CheckCircle className="w-5 h-5" />
                              : <AlertCircle className="w-5 h-5" />
                            }
                            <span className="font-semibold text-base">
                              {isConfirmed ? 'Presença Confirmada' : (consulta.status_confirmacao || 'Pendente')}
                            </span>
                          </div>
                          {consulta.data_confirmacao && (
                            <p className={`text-sm mt-1 ${
                              isConfirmed ? 'text-green-600' : 'text-yellow-600'
                            }`}>
                              Respondido em: {consulta.data_confirmacao}
                            </p>
                          )}
                          {consulta.resposta_confirmacao && (
                            <p className={`text-sm mt-2 italic ${isConfirmed ? 'text-green-600' : 'text-gray-600'}`}>
                              "{consulta.resposta_confirmacao}"
                            </p>
                          )}
                          <p className={`text-xs mt-2 ${isConfirmed ? 'text-green-400' : 'text-gray-400'}`}>
                            Lembrete de comparecimento enviado um dia antes da consulta
                          </p>
                        </div>
                      );
                    } else {
                      return (
                        <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                          <div className="flex items-center gap-2 text-gray-500">
                            <Clock className="w-5 h-5" />
                            <span className="font-medium">Aguardando confirmação</span>
                          </div>
                          <p className="text-xs text-gray-400 mt-2">
                            O lembrete de comparecimento será enviado um dia antes da consulta
                          </p>
                        </div>
                      );
                    }
                  })()}
                </div>

                {/* Observações */}
                {consulta.observacoes && (
                  <div className="mb-6">
                    <h2 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">
                      Observações
                    </h2>
                    <div className="bg-gray-50 rounded-lg p-4">
                      <p className="text-gray-700 whitespace-pre-wrap">{consulta.observacoes}</p>
                    </div>
                  </div>
                )}

                {/* Financeiro */}
                {!isBlocked && (
                  <div className="mb-6" data-testid="financeiro-section">
                    <h2 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Financeiro</h2>
                    
                    {/* Summary cards */}
                    <div className="grid grid-cols-3 gap-3 mb-4">
                      <div className="bg-gray-50 rounded-lg p-3 border border-gray-100">
                        <p className="text-xs text-gray-400 mb-1">Valor Total</p>
                        <p className="text-lg font-bold text-gray-900">{formatCurrency(valorTotal)}</p>
                      </div>
                      <div className="bg-gray-50 rounded-lg p-3 border border-gray-100">
                        <p className="text-xs text-gray-400 mb-1">Recebido</p>
                        <p className={`text-lg font-bold ${totalPago > 0 ? 'text-green-600' : 'text-gray-300'}`}>{formatCurrency(totalPago)}</p>
                      </div>
                      <div className="bg-gray-50 rounded-lg p-3 border border-gray-100">
                        <p className="text-xs text-gray-400 mb-1">Restante</p>
                        <p className={`text-lg font-bold ${(valorTotal - totalPago) > 0 ? 'text-red-600' : 'text-gray-300'}`}>{formatCurrency(Math.max(valorTotal - totalPago, 0))}</p>
                      </div>
                    </div>

                    {/* Progress bar */}
                    {valorTotal > 0 && (
                      <div className="mb-4">
                        <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                          <div
                            className={`h-full rounded-full transition-all ${totalPago >= valorTotal ? 'bg-green-500' : 'bg-amber-400'}`}
                            style={{ width: `${Math.min((totalPago / valorTotal) * 100, 100)}%` }}
                          />
                        </div>
                        <div className="flex justify-between mt-1">
                          <span className="text-xs text-gray-400">{Math.round((totalPago / valorTotal) * 100)}% pago</span>
                          <span className="text-xs text-gray-400">{formatCurrency(totalPago)} de {formatCurrency(valorTotal)}</span>
                        </div>
                      </div>
                    )}

                    {/* Lançamentos */}
                    <div className="flex items-center justify-between mb-3">
                      <p className="text-sm font-semibold text-gray-700">Lançamentos</p>
                      {!retornoInfo.is_retorno && (
                        <button
                          onClick={openLancamentoModal}
                          className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-500 hover:bg-blue-600 text-white text-xs font-semibold rounded-lg transition-colors"
                          data-testid="btn-novo-lancamento"
                        >
                          <Plus className="w-3.5 h-3.5" />
                          Novo Lançamento
                        </button>
                      )}
                    </div>

                    {retornoInfo.is_retorno && (
                      <div
                        className="mb-3 px-3 py-2 bg-purple-50 border border-purple-100 rounded-lg flex items-start gap-2 text-xs text-purple-800"
                        data-testid="financeiro-readonly-banner"
                      >
                        <RotateCcw className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
                        <span>
                          Visualização somente leitura. Os pagamentos pertencem à
                          {retornoInfo.consulta_origem_id ? (
                            <button
                              type="button"
                              onClick={() => navigate(`/consulta/${retornoInfo.consulta_origem_id}`)}
                              className="ml-1 font-semibold underline hover:text-purple-900"
                            >
                              consulta original{retornoInfo.data_consulta_origem ? ` (${formatDate(retornoInfo.data_consulta_origem)})` : ''}
                            </button>
                          ) : (
                            ' consulta original'
                          )}.
                        </span>
                      </div>
                    )}

                    {pagamentos.length > 0 ? (
                      <div className="border border-gray-100 rounded-lg overflow-x-auto">
                        <table className="w-full min-w-[480px]" data-testid="tabela-pagamentos">
                          <thead>
                            <tr className="bg-gray-50 text-xs text-gray-400 uppercase tracking-wider">
                              <th className="text-left px-4 py-2">Data</th>
                              <th className="text-left px-4 py-2">Forma</th>
                              <th className="text-left px-4 py-2">Valor</th>
                              <th className="text-left px-4 py-2">Obs</th>
                              <th className="px-4 py-2 w-10"></th>
                            </tr>
                          </thead>
                          <tbody>
                            {pagamentos.map(p => {
                              const isCancelado = !!p.cancelado;
                              return (
                                <tr
                                  key={p.id}
                                  className={`border-t border-gray-50 ${isCancelado ? 'bg-gray-50/60' : 'hover:bg-gray-50'}`}
                                  data-testid={`pagamento-row-${p.id}`}
                                >
                                  <td className={`px-4 py-3 text-sm font-medium ${isCancelado ? 'text-gray-400 line-through' : 'text-gray-800'}`}>
                                    {p.data_pagamento ? p.data_pagamento.split('-').reverse().join('/') : '-'}
                                  </td>
                                  <td className="px-4 py-3">
                                    <span className={`px-2 py-0.5 rounded text-[10px] font-bold ${
                                      isCancelado ? 'bg-gray-100 text-gray-400 line-through' :
                                      p.forma_pagamento === 'pix' ? 'bg-green-50 text-green-700' :
                                      p.forma_pagamento === 'cartao' ? 'bg-blue-50 text-blue-700' :
                                      'bg-amber-50 text-amber-700'
                                    }`}>
                                      {p.forma_pagamento === 'pix' ? 'PIX' : p.forma_pagamento === 'cartao' ? 'Cartão' : 'Dinheiro'}
                                    </span>
                                  </td>
                                  <td className={`px-4 py-3 text-sm font-semibold ${isCancelado ? 'text-gray-400 line-through' : 'text-green-600'}`}>
                                    {formatCurrency(p.valor)}
                                  </td>
                                  <td className={`px-4 py-3 text-xs max-w-[150px] truncate ${isCancelado ? 'text-gray-300 line-through' : 'text-gray-400'}`}>
                                    {p.observacao || '-'}
                                  </td>
                                  <td className="px-4 py-3 text-right">
                                    {isCancelado ? (
                                      <span
                                        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-red-50 text-red-600 text-[10px] font-bold border border-red-100"
                                        data-testid={`cancelado-badge-${p.id}`}
                                        title={p.cancelado_at ? `Cancelado em ${new Date(p.cancelado_at).toLocaleString('pt-BR')}` : 'Cancelado'}
                                      >
                                        <X className="w-2.5 h-2.5" />
                                        Cancelado
                                      </span>
                                    ) : retornoInfo.is_retorno ? (
                                      <span className="text-gray-300" title="Apenas leitura — gerenciado na consulta original">—</span>
                                    ) : (
                                      <button
                                        onClick={() => handleDeletePagamento(p.id)}
                                        className="text-gray-300 hover:text-red-500 transition-colors"
                                        data-testid={`delete-pagamento-${p.id}`}
                                        title="Cancelar lançamento"
                                      >
                                        <Trash2 className="w-3.5 h-3.5" />
                                      </button>
                                    )}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <div className="text-center py-6 bg-gray-50 rounded-lg border border-dashed border-gray-200">
                        <DollarSign className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                        <p className="text-sm text-gray-400">
                          {retornoInfo.is_retorno
                            ? 'Nenhum lançamento na consulta original'
                            : 'Nenhum lançamento registrado'}
                        </p>
                      </div>
                    )}
                  </div>
                )}

                {/* Action Buttons */}
                {canStartAppointment && consulta.paciente_id && (
                  <div className="border-t border-gray-100 pt-6 mt-6">
                    <div className="flex flex-col sm:flex-row gap-3">
                      <Button
                        onClick={handleIniciarAtendimento}
                        className="flex-1 bg-green-600 hover:bg-green-700 text-white py-3 text-base"
                        data-testid="iniciar-atendimento-btn"
                      >
                        <HeartPulse className="w-5 h-5 mr-2" />
                        Iniciar Atendimento
                      </Button>
                      
                      <Button
                        variant="outline"
                        onClick={() => setShowFaltaModal(true)}
                        className="flex-1 border-amber-300 text-amber-700 hover:bg-amber-50 py-3 text-base"
                        data-testid="marcar-falta-btn"
                      >
                        <UserX className="w-5 h-5 mr-2" />
                        Marcar Falta
                      </Button>
                      
                      <Button
                        variant="outline"
                        onClick={() => setShowCancelModal(true)}
                        className="flex-1 border-red-300 text-red-600 hover:bg-red-50 py-3 text-base"
                        data-testid="cancelar-consulta-btn"
                      >
                        <XCircle className="w-5 h-5 mr-2" />
                        Cancelar Consulta
                      </Button>
                    </div>
                  </div>
                )}

                {/* Mensagem se consulta já realizada */}
                {consulta.status === 'realizada' && (
                  <div className="border-t border-gray-100 pt-6 mt-6">
                    <div className="bg-gray-100 rounded-lg p-4 text-center">
                      <CheckCircle className="w-8 h-8 text-gray-500 mx-auto mb-2" />
                      <p className="text-gray-600">Esta consulta já foi realizada</p>
                      {consulta.data_realizacao && (
                        <p className="text-sm text-gray-500">em {formatDate(consulta.data_realizacao)}</p>
                      )}
                    </div>
                  </div>
                )}

                {/* Mensagem se consulta cancelada */}
                {consulta.status === 'cancelada' && (
                  <div className="border-t border-gray-100 pt-6 mt-6">
                    <div className="bg-red-50 rounded-lg p-4 text-center">
                      <XCircle className="w-8 h-8 text-red-500 mx-auto mb-2" />
                      <p className="text-red-600">Esta consulta foi cancelada</p>
                      {consulta.motivo_cancelamento && (
                        <p className="text-sm text-red-500 mt-1">
                          {consulta.motivo_cancelamento === 'cancelado_paciente' ? 'Cancelada pelo paciente' : 'Cancelada pelo médico'}
                        </p>
                      )}
                    </div>
                  </div>
                )}

                {/* Mensagem se paciente faltou */}
                {consulta.status === 'falta' && (
                  <div className="border-t border-gray-100 pt-6 mt-6">
                    <div className="bg-amber-50 rounded-lg p-4 text-center">
                      <UserX className="w-8 h-8 text-amber-500 mx-auto mb-2" />
                      <p className="text-amber-700">Paciente não compareceu</p>
                    </div>
                  </div>
                )}
              </>
            )}

            {/* Observações para bloqueio */}
            {isBlocked && consulta.observacoes && (
              <div className="mb-6">
                <h2 className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-3">
                  Motivo do Bloqueio
                </h2>
                <div className="bg-gray-50 rounded-lg p-4">
                  <p className="text-gray-700">{consulta.observacoes}</p>
                </div>
              </div>
            )}

            {/* Botão Excluir Bloqueio - para qualquer usuario */}
            {isBlocked && (
              <div className="border-t border-gray-100 pt-6 mt-6">
                <Button
                  onClick={handleExcluirBloqueio}
                  className="w-full bg-red-600 hover:bg-red-700 text-white py-3 text-base"
                  disabled={processing}
                  data-testid="excluir-bloqueio-btn"
                >
                  {processing ? (
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                  ) : (
                    <Trash2 className="w-5 h-5 mr-2" />
                  )}
                  Excluir Bloqueio
                </Button>
              </div>
            )}

            {/* Botão Excluir - Apenas Master */}
            {isMasterAccess && (
              <div className="border-t border-red-200 pt-6 mt-6">
                <Button
                  onClick={() => setShowExcluirModal(true)}
                  className="w-full bg-red-700 hover:bg-red-800 text-white py-3 text-base font-semibold"
                  data-testid="master-excluir-consulta-detalhes-btn"
                >
                  <Trash2 className="w-5 h-5 mr-2" />
                  Excluir Consulta Definitivamente
                </Button>
                <p className="text-xs text-red-500 text-center mt-2">
                  Visível apenas para administrador master. A exclusão é irreversível.
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Protocolo */}
        {consulta.event_sequence_id && (
          <div className="mt-4 text-center">
            <span className="text-xs text-gray-400">
              Protocolo: {consulta.event_sequence_id}
            </span>
          </div>
        )}
      </div>

      {/* Modal de Confirmação - Iniciar Atendimento */}
      {showConfirmModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6 animate-in fade-in zoom-in duration-200">
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <HeartPulse className="w-8 h-8 text-green-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900">
                Deseja iniciar o atendimento com o paciente?
              </h3>
            </div>

            <div className="flex flex-col sm:flex-row gap-3">
              <Button
                variant="outline"
                onClick={() => setShowConfirmModal(false)}
                className="flex-1 py-3"
                disabled={processing}
              >
                <X className="w-4 h-4 mr-2" />
                Sair
              </Button>
              <Button
                onClick={handleConfirmarAtendimento}
                className="flex-1 py-3 bg-green-600 hover:bg-green-700 text-white"
                disabled={processing}
                data-testid="confirmar-atendimento-btn"
              >
                {processing ? (
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                ) : (
                  <HeartPulse className="w-4 h-4 mr-2" />
                )}
                Atender Paciente
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Cancelamento (componente reutilizável) */}
      {showCancelModal && (
        <CancelarConsultaModal
          consulta={consulta}
          onClose={() => setShowCancelModal(false)}
          onCancelled={handleCancelledByModal}
        />
      )}

      {/* Modal de Falta */}
      {showFaltaModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6 animate-in fade-in zoom-in duration-200">
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <UserX className="w-8 h-8 text-amber-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                Marcar como Falta
              </h3>
              <p className="text-gray-600">
                Confirma que o paciente <strong>{consulta.paciente_nome}</strong> não compareceu à consulta?
              </p>
            </div>

            <div className="flex flex-col sm:flex-row gap-3">
              <Button
                variant="outline"
                onClick={() => setShowFaltaModal(false)}
                className="flex-1 py-3"
                disabled={processing}
              >
                Voltar
              </Button>
              <Button
                onClick={handleMarcarFalta}
                className="flex-1 py-3 bg-amber-500 hover:bg-amber-600 text-white"
                disabled={processing}
              >
                {processing ? (
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                ) : (
                  <UserX className="w-4 h-4 mr-2" />
                )}
                Confirmar Falta
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Exclusão Master */}
      {showExcluirModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6 animate-in fade-in zoom-in duration-200">
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Trash2 className="w-8 h-8 text-red-700" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                Excluir Consulta Permanentemente
              </h3>
              <p className="text-gray-600 mb-3">
                Tem certeza que deseja excluir a consulta de <strong>{consulta.paciente_nome}</strong>?
              </p>
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-left text-sm text-red-700">
                <p className="font-semibold mb-1">Esta acao e irreversivel:</p>
                <ul className="list-disc list-inside space-y-1">
                  <li>A consulta sera removida da agenda e do Google Calendar</li>
                  <li>Retornos vinculados serao ajustados automaticamente</li>
                  <li>Status atual: <strong>{consulta.status}</strong></li>
                </ul>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-3">
              <Button
                variant="outline"
                onClick={() => setShowExcluirModal(false)}
                className="flex-1 py-3"
                disabled={processing}
              >
                Cancelar
              </Button>
              <Button
                onClick={handleExcluirConsulta}
                className="flex-1 py-3 bg-red-700 hover:bg-red-800 text-white"
                disabled={processing}
                data-testid="confirmar-exclusao-master-btn"
              >
                {processing ? (
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                ) : (
                  <Trash2 className="w-4 h-4 mr-2" />
                )}
                Excluir Definitivamente
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Novo Lançamento Financeiro */}
      {showLancamentoModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" data-testid="lancamento-modal">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6 animate-in fade-in zoom-in duration-200">
            <div className="flex items-center justify-between mb-5">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                  <DollarSign className="w-5 h-5 text-blue-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900">Novo Lançamento</h3>
              </div>
              <button
                onClick={() => setShowLancamentoModal(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
                data-testid="close-lancamento-modal"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-4">
              {/* Valor Total (editável + salvar independente) */}
              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">
                  Valor Total da Consulta
                </label>
                <div className="flex gap-2">
                  <div className="relative flex-1">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm font-medium">R$</span>
                    <input
                      ref={valorTotalInputRef}
                      type="text"
                      inputMode="decimal"
                      value={valorTotalEdit}
                      onChange={(e) => setValorTotalEdit(e.target.value)}
                      placeholder="0,00"
                      className={`w-full pl-10 pr-3 py-2.5 border rounded-lg focus:ring-2 focus:border-transparent outline-none text-sm ${
                        valorTotalErro ? 'border-red-300 focus:ring-red-400' : 'border-gray-200 focus:ring-blue-400'
                      }`}
                      data-testid="input-valor-total"
                    />
                  </div>
                  {(() => {
                    const vt = parseFloat((valorTotalEdit || '').replace(/[^\d.,]/g, '').replace(',', '.'));
                    const changed = !isNaN(vt) && Math.abs(vt - valorTotal) > 0.001;
                    return (
                      <button
                        type="button"
                        onClick={handleSalvarValorTotalButton}
                        disabled={!changed || !!valorTotalErro || savingValorTotal}
                        className="px-4 py-2.5 bg-slate-700 hover:bg-slate-800 disabled:bg-gray-200 disabled:text-gray-400 disabled:cursor-not-allowed text-white text-xs font-semibold rounded-lg transition-colors whitespace-nowrap"
                        data-testid="btn-salvar-valor-total"
                      >
                        {savingValorTotal ? '...' : 'Salvar'}
                      </button>
                    );
                  })()}
                </div>
                {valorTotalErro ? (
                  <p className="text-[11px] text-red-600 mt-1 flex items-center gap-1" data-testid="valor-total-erro">
                    <AlertCircle className="w-3 h-3" />
                    {valorTotalErro}
                  </p>
                ) : (
                  <p className="text-[11px] text-gray-400 mt-1">Edite se o procedimento incluir exames ou adicionais</p>
                )}
              </div>

              {/* Valor do Pagamento */}
              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">
                  Valor Recebido <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm font-medium">R$</span>
                  <input
                    ref={valorRecebidoInputRef}
                    type="text"
                    inputMode="decimal"
                    value={lancamentoForm.valor}
                    onChange={(e) => setLancamentoForm({ ...lancamentoForm, valor: e.target.value })}
                    placeholder="0,00"
                    className={`w-full pl-10 pr-3 py-2.5 border rounded-lg focus:ring-2 focus:border-transparent outline-none text-sm ${
                      lancamentoErro ? 'border-red-300 focus:ring-red-400' : 'border-gray-200 focus:ring-blue-400'
                    }`}
                    data-testid="input-valor-pagamento"
                  />
                </div>
                {(() => {
                  const vtEdit = parseFloat((valorTotalEdit || '').replace(/[^\d.,]/g, '').replace(',', '.'));
                  const vtEfetivo = (!isNaN(vtEdit) && vtEdit > 0) ? vtEdit : valorTotal;
                  const restanteAtual = Math.max(vtEfetivo - totalPago, 0);
                  if (vtEfetivo > 0) {
                    return (
                      <p className="text-[11px] text-gray-400 mt-1" data-testid="restante-disponivel">
                        Restante disponível: <span className="font-semibold text-gray-600">{formatCurrency(restanteAtual)}</span>
                      </p>
                    );
                  }
                  return null;
                })()}
              </div>

              {/* Forma de Pagamento */}
              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">
                  Forma de Pagamento
                </label>
                <div className="grid grid-cols-3 gap-2">
                  {[
                    { value: 'pix', label: 'PIX' },
                    { value: 'cartao', label: 'Cartão' },
                    { value: 'dinheiro', label: 'Dinheiro' }
                  ].map(opt => (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => setLancamentoForm({ ...lancamentoForm, forma_pagamento: opt.value })}
                      className={`py-2 px-3 rounded-lg text-sm font-semibold border transition-colors ${
                        lancamentoForm.forma_pagamento === opt.value
                          ? 'bg-blue-500 text-white border-blue-500'
                          : 'bg-white text-gray-600 border-gray-200 hover:border-gray-300'
                      }`}
                      data-testid={`forma-${opt.value}`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Data */}
              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">
                  Data do Pagamento
                </label>
                <input
                  type="date"
                  value={lancamentoForm.data_pagamento}
                  onChange={(e) => setLancamentoForm({ ...lancamentoForm, data_pagamento: e.target.value })}
                  className="w-full px-3 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-400 focus:border-transparent outline-none text-sm"
                  data-testid="input-data-pagamento"
                />
              </div>

              {/* Observação */}
              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">
                  Observação <span className="text-gray-300 font-normal normal-case">(opcional)</span>
                </label>
                <textarea
                  value={lancamentoForm.observacao}
                  onChange={(e) => setLancamentoForm({ ...lancamentoForm, observacao: e.target.value })}
                  placeholder="Ex: Sinal via WhatsApp"
                  rows={2}
                  className="w-full px-3 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-400 focus:border-transparent outline-none text-sm resize-none"
                  data-testid="input-observacao"
                />
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <Button
                variant="outline"
                onClick={() => setShowLancamentoModal(false)}
                className="flex-1 py-2.5"
                disabled={savingLancamento}
              >
                Cancelar
              </Button>
              <Button
                onClick={handleSaveLancamento}
                className="flex-1 py-2.5 bg-blue-500 hover:bg-blue-600 text-white disabled:bg-gray-300 disabled:cursor-not-allowed"
                disabled={savingLancamento || !!lancamentoErro || !!valorTotalErro}
                data-testid="salvar-lancamento-btn"
              >
                {savingLancamento ? (
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                ) : (
                  <Plus className="w-4 h-4 mr-1.5" />
                )}
                Registrar
              </Button>
            </div>

            {lancamentoErro && (
              <div
                className="mt-3 px-3 py-2 bg-red-50 border border-red-200 rounded-lg text-xs text-red-700 font-medium flex items-start gap-2"
                data-testid="lancamento-erro"
              >
                <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                <span>{lancamentoErro}</span>
              </div>
            )}
          </div>
        </div>
      )}

    </Layout>
  );
}
